import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, {useCallback, useMemo, useRef} from "react";
import { aggridHeaderClass, aggridSwitchButtonWrapperStyle } from "src/components/common/common";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import {
    CreateCellClickHandler, CustomCellClickAction, createCellCssClassRules, createGridDropDownCommitAfter,
    getSwitchButtonColumnOptions, GridCellConfig, postSortHandler, RemoveRowHandler, useRowSelectionHandler,
} from "src/utils/grid/GridHandlerContent";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { showWarning } from "src/utils/toast/toastUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { HRM2130_R01Dto } from "./Dto_HRM2130_R01";
import { useHRM2130_R01Store } from "./Store_HRM2130_R01";
import { ATTEND_GROUP_OUTING, headerNames_HRM2130_R01 } from "./Util_HRM2130_R01";
import { useHRM2130_R01CellChange } from "./hooks/HRM2130_R01CellChange";

// 이 화면의 컬럼 배치·너비를 브라우저에 보관할 때 사용하는 키이다.
const COLUMN_STATE_KEY = 'HRM2130_R01-grid-column-state';

/** Container에서 전달받는 값의 모양이다. */
interface Props {
    clickable: boolean; // true이면 행 클릭을 선택으로 처리한다. +/- 버튼 직후에는 false가 되어 같은 클릭을 한 번 무시한다.
    setClickable: (clickable: boolean) => void; // clickable 값을 바꾸는 함수
    gridRef: React.RefObject<AgGridReact<HRM2130_R01Dto> | null>; // 그리드 인스턴스를 가리키는 ref. 편집 확정·스크롤 이동에 사용한다.
    defaultHRM2130_R01Row: HRM2130_R01Dto; // 신규 행과 빈 행을 만들 때 쓰는 기본값
    handleInsertClick: (event: React.MouseEvent, rowIndex?: number) => void; // 행 추가 함수. rowIndex가 있으면 해당 행 아래에 추가한다.
}

/**
 * 근태코드 목록을 편집 가능한 단일 그리드로 렌더링한다.
 * @param gridRef Content와 공유하여 편집 확정 및 행 이동에 사용한다.
 * @param defaultHRM2130_R01Row 신규 행과 그리드 기본 행의 초기값이다.
 * @returns 컬럼 상태, 선택·삭제 표시, 셀 편집 이벤트가 연결된 그리드 JSX
 */
export const Table_HRM2130_R01 = (props : Props) => {

    // 구조 분해: props 객체에서 필요한 값을 각각의 변수로 꺼낸다.
    const {
        clickable, setClickable, gridRef, defaultHRM2130_R01Row, handleInsertClick,
    } = props;

    // 화면 Store에서 목록, 선택·삭제 식별값 목록, 원본 목록, 컬럼 상태, 스크롤 위치와 변경 함수를 가져온다.
    const {
        HRM2130_R01List, setHRM2130_R01List, removeHRM2130_R01List, updateHRM2130_R01List,
        selectedHRM2130_R01List, addSelectedHRM2130_R01List, removeSelectedHRM2130_R01List,
        initSelectedHRM2130_R01List, allSelectedHRM2130_R01List,
        deletedHRM2130_R01List, addDeletedHRM2130_R01List,
        originalHRM2130_R01List,
        columnState, setColumnState, scrollTop, setScrollTop,
    } = useHRM2130_R01Store();
    // 공통코드 Store에서 근태구분 드롭다운 선택 목록을 가져온다.
    const { attendGroupList } = useCommonCodeStore();

    const isRowDraggingRef = useRef(false);
    /**
     * 스위치 변경을 Store에 반영한다. G5 이외의 근무인정 변경은 Y로 되돌리고 종료한다.
     * @param index 변경할 행의 valueIndex
     * @param data 스위치 값이 반영된 행 데이터
     * @returns 반환값은 없다.
     */
    const updateRow = useCallback((index: number, data: HRM2130_R01Dto) => {
        // 공통 스위치 핸들러는 셀 변경 이벤트를 거치지 않으므로 여기에서도 G5 제한을 검사한다.
        if (data.attendGroup !== ATTEND_GROUP_OUTING && data.workYn !== 'Y') {
            showWarning('근무인정은 외출(G5) 코드에서만 변경할 수 있습니다.');
            // 펼치기(...): data를 복사한 뒤 workYn만 'Y'로 덮어쓴 새 객체를 만든다.
            updateHRM2130_R01List(index, { ...data, workYn: 'Y' });
            return;
        }
        updateHRM2130_R01List(index, data);
    }, [updateHRM2130_R01List]);

    // 그리드 기본 설정: 행 ID 생성, 행·셀 CSS 규칙 생성 함수, 빈 행을 채운 표시 목록, 행 번호 칸 렌더러, 스위치 핸들러를 받는다.
    const {
        getRowId, createRowClassRules, createCellClassRules, paddedGridData,
        renderIndexCell, handleSwitchButtonHandler,
    } = useGridSettingContent<HRM2130_R01Dto>({
        gridRef,
        // 각 행을 구분하는 기준 필드
        key: 'valueIndex',
        gridData: HRM2130_R01List,
        defaultGridData: defaultHRM2130_R01Row,
        selectedRow: selectedHRM2130_R01List,
        originalGridData: originalHRM2130_R01List,
        updateRow,
    });

    // 신규·기존 행별 비활성 셀과 포인터·정렬 스타일을 정의한다.
    // 의존성이 []이므로 처음 한 번만 만들고 같은 객체를 계속 사용한다.
    const cssCellConfig = useMemo<GridCellConfig>(() => ({
        // 모든 행에서 비활성(회색)으로 표시할 컬럼
        disabledCells: [],
        // 신규 행에서만 비활성으로 표시할 컬럼
        insertedDisabledCells: [],
        // 기존 행에서만 비활성으로 표시할 컬럼. 저장된 근태코드는 기본키라서 바꿀 수 없다.
        commonDisabledCells: ['attendCd'],
        // 모든 행에서 손가락 커서를 표시할 컬럼
        pointerCells: ['attendNm', 'attendGroup', 'useYn', 'workYn', 'sortSeq', 'remark'],
        // 신규 행에서만 손가락 커서를 표시할 컬럼
        insertedPointerCells: ['attendCd'],
        // 기존 행에서만 손가락 커서를 표시할 컬럼
        commonPointerCells: [],
        // 왼쪽 정렬할 컬럼
        alignLeftCells: ['attendCd', 'attendNm', 'remark'],
    }), []);

    // 위 설정을 신규 행용·기존 행용 목록으로 합친 결과를 구조 분해로 꺼낸다.
    const {
        disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns,
    } = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

    // 모든 컬럼에 적용할 기본 설정에 셀 CSS 규칙(선택·삭제·비활성·포인터·정렬)을 더한 객체
    // 선택·삭제 값은 문자열로 저장되므로 숫자 valueIndex와 비교할 때 숫자로 바꾼다.
    const cellCssDefaultColDef = useMemo(() => ({
        ...defaultColDef,
        cellClassRules: createCellClassRules<HRM2130_R01Dto>({
            idKey: 'valueIndex',
            selectedList: selectedHRM2130_R01List.map(Number),
            deletedList: deletedHRM2130_R01List.map(Number),
            disabledInsertedColumns, disabledColumns,
            pointerInsertedColumns, pointerColumns, alignLeftColumns,
        }),
    }), [createCellClassRules, selectedHRM2130_R01List, deletedHRM2130_R01List,
        disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns]);

    // 클릭·키보드로 편집을 시작할 수 있는 컬럼 목록. 처음 한 번만 만든다.
    const editableColumns = useMemo(() => ['attendCd', 'attendNm', 'attendGroup', 'sortSeq', 'remark'], []);
    // 드롭다운에서 Enter/Tab을 누르면 다음 편집 가능 셀로 포커스를 옮긴다.
    const handleGridDropDownCommitAfter = useMemo(
        () => createGridDropDownCommitAfter<HRM2130_R01Dto>(editableColumns),
        [editableColumns],
    );

    /**
     * 그리드 컬럼 정의를 반환한다. 근태구분은 코드명을 표시하고 minorCd를 값으로 유지한다.
     * 근태코드는 신규 행에서만 편집하며, 근무인정 스위치는 G5에서만 활성화한다.
     */
    const baseColumnDefs = useMemo<ColDef<HRM2130_R01Dto>[]>(() => [
        // 펼치기(...): 행 번호 컬럼 설정 배열의 항목을 이 배열 안에 그대로 풀어 넣는다.
        ...getDefaultRowIndexColumnConfig(
            // 행 번호 칸 렌더러: 번호 또는 D·I·U 표시와 +/- 버튼을 그린다.
            (params: HRM2130_R01Dto) => renderIndexCell(
                params,
                // 행 상태 판단: 공통 함수가 삭제·선택·신규·수정 여부를 계산해 반환한다.
                // 공통 함수는 문자열 식별값 목록과 숫자 valueIndex를 그대로 비교하므로 이 화면에서는 D 표시·선택 배경이 행 번호 칸에 나타나지 않는다. (사용자 결정으로 PRD2010_R01 표준과 동일하게 적용)
                (innerParams: HRM2130_R01Dto) => getRowStyleFlags(
                    innerParams,
                    {
                        deletedList: deletedHRM2130_R01List,
                        selectedList: selectedHRM2130_R01List,
                        dataList: HRM2130_R01List,
                    }),
                // + 버튼: 해당 행 아래에 신규 행을 추가한다. Ctrl을 누른 채 클릭하면 10행을 한 번에 추가한다.
                (rowIndex: number, e: React.MouseEvent) => {
                    if (e.ctrlKey) {
                        for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex); }
                    } else {
                        handleInsertClick(e, rowIndex);
                    }
                },
                // - 버튼: 공통 함수가 마지막 1행 삭제를 막고, 신규 행은 즉시 제거·기존 행은 삭제 표시한다.
                (rowData) => RemoveRowHandler(
                    gridRef,
                    HRM2130_R01List,
                    rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
                    setClickable,
                    removeHRM2130_R01List,
                    // 삭제 표시 목록은 문자열 식별값으로 저장한다.
                    (valueIndex) => addDeletedHRM2130_R01List([valueIndex.toString()])
                ),
            ),
        ),
        {
            headerName: '근태코드', colId: 'attendCd', field: 'attendCd', width: 100,
            // 이미 저장된 행의 기본키는 편집하지 않는다.
            editable: params => params.data?.inserted === true,
            pasteable: true,
            // ??: 값이 null·undefined이면 빈 문자열을 쓴다. 앞뒤 공백을 지우고 대문자로 바꿔 저장한다.
            valueParser: params => String(params.newValue ?? '').trim().toUpperCase(),
        },
        { headerName: '근태명', colId: 'attendNm', field: 'attendNm', width: 160, editable: true, pasteable: true },
        {
            headerName: '근태구분', colId: 'attendGroup', field: 'attendGroup', width: 120,
            // 공통 그리드 드롭다운을 사용해 코드명으로 보여 주고 minorCd를 저장한다.
            ...CommonCodeColumn.options<HRM2130_R01Dto>(
                attendGroupList,
                // 모든 행에서 편집 가능
                true,
                CommonCodeColumn.change(updateHRM2130_R01List, {
                    // 외출(G5)에서 다른 구분으로 바꾸면 근무인정 값을 Y로 함께 되돌린다.
                    // 삼항 연산자: G5이면 추가 변경 없이 빈 패치를, 그 외 구분이면 근무인정 Y 패치를 반환한다.
                    getPatch: ({ newValue }) => newValue === ATTEND_GROUP_OUTING ? {} : { workYn: 'Y' },
                }),
                handleGridDropDownCommitAfter,
            ),
        },
        {
            headerName: '사용', colId: 'useYn', field: 'useYn', width: 80,
            // 사용 여부 스위치 컬럼. 마지막 true는 모든 행에서 조작 가능하다는 뜻이다.
            ...getSwitchButtonColumnOptions<HRM2130_R01Dto>('useYn', handleSwitchButtonHandler,
                headerNames_HRM2130_R01, aggridSwitchButtonWrapperStyle, true),
        },
        {
            headerName: '근무인정', colId: 'workYn', field: 'workYn', width: 90,
            ...getSwitchButtonColumnOptions<HRM2130_R01Dto>('workYn', handleSwitchButtonHandler,
                headerNames_HRM2130_R01, aggridSwitchButtonWrapperStyle,
                // G5 행만 근무인정 스위치를 조작할 수 있다.
                params => params.data?.attendGroup === ATTEND_GROUP_OUTING),
            // G5가 아닌 행은 근무인정 칸을 회색으로 표시한다.
            cellClassRules: {
                'disabled-cell': params => params.data?.attendGroup !== ATTEND_GROUP_OUTING,
            },
        },
        {
            headerName: '순서', colId: 'sortSeq', field: 'sortSeq', width: 80, editable: true,
            // 입력 순서에서 쉼표·공백을 제거하고 숫자로 바꾼다. 숫자가 아니면 0을 반환한다.
            // 정규식 /,/g: 문자열 안의 모든 쉼표를 찾는다.
            valueParser: params => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
        {
            headerName: '비고', colId: 'remark', field: 'remark', width: 320, editable: true, pasteable: true,
            // 기본 텍스트 편집기를 쓰고 최대 1000자까지 입력받는다.
            cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 1000 },
        },
    ], [renderIndexCell, deletedHRM2130_R01List, selectedHRM2130_R01List, HRM2130_R01List,
        handleInsertClick, gridRef, setClickable, removeHRM2130_R01List, addDeletedHRM2130_R01List,
        attendGroupList, updateHRM2130_R01List,
        handleGridDropDownCommitAfter, handleSwitchButtonHandler]);

    // 기본 컬럼 순서를 생성하고 저장된 컬럼 상태가 있을 때만 그 상태를 적용한다.
    const defaultColumnState = useMemo(() => buildDefaultColumnStateFromDefs(baseColumnDefs, []), [baseColumnDefs]);
    // 삼항 연산자: columnState가 없으면 기본 정의를, 있으면 사용자가 저장한 배치를 반영한 정의를 반환한다.
    const columnDefs = useMemo(() => columnState === null ? baseColumnDefs : GridCustomColumnDefs<HRM2130_R01Dto>({
        baseColumnDefs, columnState, headerNames: headerNames_HRM2130_R01,
        fallbackHeaderClass: aggridHeaderClass,
    }), [baseColumnDefs, columnState]);

    // 셀 값이 바뀌었을 때 Store에 반영하는 함수를 가져온다.
    const { handleCellChange } = useHRM2130_R01CellChange({ updateHRM2130_R01List });
    // 행 클릭 선택 토글 핸들러: 공통 Hook이 합계 행·삭제 행을 제외하고 선택을 추가·해제한다.
    // 공통 Hook은 valueIndex를 숫자 그대로 저장하므로, 선택 목록에는 숫자(행 클릭)와 문자열(전체 선택)이 함께 들어올 수 있다. (PRD2010_R01 표준과 동일)
    // 의존성: 선택·삭제 목록과 clickable이 바뀌면 핸들러를 다시 만든다.
    const handleRowClick = useRowSelectionHandler({
        dataList: HRM2130_R01List,
        idKey: 'valueIndex',
        selectedList: selectedHRM2130_R01List,
        deletedList: deletedHRM2130_R01List,
        addSelected: addSelectedHRM2130_R01List,
        removeSelected: removeSelectedHRM2130_R01List,
        clickable, setClickable,
    });

    // 방향키 이동과 편집 가능 컬럼 내 키보드 탐색을 공통 이벤트 훅에 연결한다.
    const cellEventHandlers = useDetailTableEvents({
        detailList: HRM2130_R01List, editableColumns,
        keyDownConfig: { enableArrowNavigation: true },
    });

    // 셀 클릭 동작 목록: 일반 입력 컬럼을 클릭하면 셀 편집을 시작한다. 코드 칸은 신규 행에서만 허용한다.
    const cellClickActions = useMemo<CustomCellClickAction<HRM2130_R01Dto>[]>(() => [{
        // 편집 가능 컬럼이면서 코드 칸은 신규 행일 때만 true를 반환한다.
        validate: (field, data) => editableColumns.includes(field) &&
            (field !== 'attendCd' || data.inserted),
        // 유효한 클릭의 셀 편집을 시작한다. 반환값은 없다.
        // !: rowIndex와 field가 반드시 있다고 TypeScript에 알려 준다.
        action: event => event.api.startEditingCell({ rowIndex: event.rowIndex!, colKey: event.colDef.field! }),
    }], [editableColumns]);

    // 클릭한 셀에 맞는 동작을 찾아 실행하는 셀 클릭 핸들러
    const handleCellClick = CreateCellClickHandler(HRM2130_R01List, 'valueIndex', cellClickActions);
    /**
     * 헤더 정렬 후 원본·현재 행 인덱스를 기준으로 표시 순서를 맞춘다.
     * @param params 정렬된 행 정보
     * @returns 반환값은 없다.
     */
    const handlePostSortWithDrag = useCallback((params: PostSortRowsParams<HRM2130_R01Dto>) => {
        if (isRowDraggingRef.current) return
        postSortHandler(params, HRM2130_R01List, originalHRM2130_R01List, setHRM2130_R01List);
    }, [HRM2130_R01List, originalHRM2130_R01List, setHRM2130_R01List]);

    // 하단 고정 행에는 현재 표시 목록 건수를 표시한다.
    const pinnedBottomRow = useMemo(() => [{
        attendCd: HRM2130_R01List.length.toLocaleString() + '건',
        editable: false,
    }], [HRM2130_R01List]);

    // 계산·선택·정렬·편집 이벤트와 컬럼 상태를 연결한 JSX를 반환한다.
    return (
        // 우클릭 메뉴(전체 선택·선택 해제), 셀 계산, 컬럼 상태 저장을 담당하는 공통 감싸기 컴포넌트
        <GridContextWrapper<HRM2130_R01Dto>
            enableCellCalculation
            gridRef={gridRef}
            paddedGridData={paddedGridData}
            initSelected={initSelectedHRM2130_R01List}
            defaultRowData={defaultHRM2130_R01Row}
            allSelected={allSelectedHRM2130_R01List}
            baseColumnDefs={columnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setColumnState}
            localStorageKey={COLUMN_STATE_KEY}
            menuItems={undefined}
        >
            {/* 실제 편집 그리드: 행 데이터, 컬럼 정의, 행·셀 CSS 규칙, 클릭·키보드·값 변경·정렬 이벤트를 연결한다. */}
            <Grid_Excel<HRM2130_R01Dto>
                ref={gridRef}
                rowData={paddedGridData}
                getRowId={getRowId}
                columnDefs={columnDefs}
                defaultColDef={cellCssDefaultColDef}
                rowClassRules={createRowClassRules<HRM2130_R01Dto>({
                    idKey: 'valueIndex',
                    selectedList: selectedHRM2130_R01List.map(Number),
                    deletedList: deletedHRM2130_R01List.map(Number),
                })}
                onRowClicked={handleRowClick}
                onCellClicked={handleCellClick}
                onCellKeyDown={cellEventHandlers.handleCellKeyDown}
                onCellValueChanged={handleCellChange}
                pinnedBottomRowData={pinnedBottomRow}
                onPostSortRows={handlePostSortWithDrag}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setColumnState}
                getDefaultColumnState={() => defaultColumnState}
                scrollTop={scrollTop}
                setScrollTop={setScrollTop}
            />
        </GridContextWrapper>
    );
};
